import { Radio, RadioGroup } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { motion } from 'motion/react'
import { Fragment, ReactNode, useId } from 'react'

export type WidgetSelectGroupItem<T extends string> = {
	id: T
	render: () => ReactNode
	label: string
}

export type WidgetSelectGroupProps<T extends string> = {
	value: T | (string & {})
	onChange: (value: T) => void
	items: Array<WidgetSelectGroupItem<T>>
}

export function WidgetSelectGroup<T extends string>({ value, onChange, items }: WidgetSelectGroupProps<T>) {
	const uid = useId()

	const itemClassName = ctl(
		` rounded-lg relative px-2 py-1 flex-1 text-center flex items-center justify-center text-sm`,
	)
	const selectedItemClassName = 'text-grey-900'
	const unselectedItemClassName = 'text-grey-500'

	return (
		<motion.div layout transition={{ layout: { duration: 0 } }}>
			<RadioGroup
				value={value}
				onChange={onChange}
				className="flex items-center rounded-lg bg-grey-100 p-0.5"
			>
				{items.map(({ id, render, label }, index) => (
					<Fragment key={id}>
						{/* Show this div only if this item is not selected, nor the one before, nor is the first. */}
						<div
							className={ctl(`
							w-[1px] bg-grey-200 self-stretch my-1 
							${
								(value !== id && value !== items[index - 1]?.id && index !== 0)
									? 'opacity-100'
									: 'opacity-0'
							}
							`)}
						/>
						<Radio
							key={id}
							value={id}
							className={ctl(`
                                relative cursor-pointer
								flex-1
                                ${itemClassName}
								${value === id ? selectedItemClassName : unselectedItemClassName}
                            `)}
						>
							{value === id && (
								<motion.div
									layoutId={`selected-${uid}`}
									initial={false}
									transition={{
										duration: 0.2,
										type: 'spring',
										bounce: 0,
									}}
									className="absolute inset-0 bg-white shadow-sm rounded-lg"
								/>
							)}
							<div className="relative z-10 whitespace-nowrap">
								{render()}
							</div>
						</Radio>

						{/* Show this div only if this item is not selected, nor the one after, nor is the last. */}
						<div
							className={ctl(`
							w-[1px] bg-grey-200 self-stretch my-1 
							relative left-[1px]
							${
								(value !== id && value !== items[index + 1]?.id && index !== items.length - 1)
									? 'opacity-100'
									: 'opacity-0'
							}
							`)}
						/>
					</Fragment>
				))}
			</RadioGroup>
		</motion.div>
	)
}
